import { GridColDef } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { ReactNode, createContext, useContext, useMemo, useState } from "react";

import { useGetVehiclesQuery } from "@/requests/transport.queries";
import { WeTrackVehicleData } from "@/types/wetrack.types";

import { ActionsColumn } from "./VehicleTable/columns/ActionsColumn";
import { AddressColumn } from "./VehicleTable/columns/AddressColumn";
import { BrandColumn } from "./VehicleTable/columns/BrandColumn";
import { DailyKm } from "./VehicleTable/columns/DailyKmColumn";
import { DriverColumn } from "./VehicleTable/columns/DriverColumn";
import { LastIgnitionDate } from "./VehicleTable/columns/LastIgnitionDate";
import { LastUpdateDate } from "./VehicleTable/columns/LastUpdateColumn";
import { PlateColumn } from "./VehicleTable/columns/PlateColumn";
import { SetQrCodeVehicleType, VehiclesContextType } from "./types";

export const VehiclesContext = createContext<VehiclesContextType | null>(null);

export const VehiclesProvider = ({ children }: { children: ReactNode }) => {
    const { data, isLoading: loading, refetch } = useGetVehiclesQuery(false);

    const vehicles = useMemo(() => (data ? data.map((item) => item.properties) : []), [data]);
    const [qrCodeVehicle, setQrCodeVehicle] = useState<SetQrCodeVehicleType | null>(null);
    const [showPrintPage, setShowPrintPage] = useState(false);

    const api = useGridApiRef();
    const columns: GridColDef[] = [
        DriverColumn({ avatar: true }),
        PlateColumn<WeTrackVehicleData["properties"]>({ getPlate: (row) => row.plate || "" }),
        BrandColumn<WeTrackVehicleData["properties"]>({
            getBrand: (row) => row.vehicle_brand,
        }),
        DailyKm(),
        LastUpdateDate(),
        LastIgnitionDate(),
        AddressColumn(),
        ActionsColumn(setQrCodeVehicle),
    ];

    const [rerender] = useState(0);

    return (
        <VehiclesContext.Provider
            value={{
                rows: vehicles,
                loading,
                columns,
                api,
                rerender,
                reloadTable: refetch,
                qrCodeVehicle,
                setQrCodeVehicle,
                setShowPrintPage,
                showPrintPage,
            }}
        >
            {children}
        </VehiclesContext.Provider>
    );
};

export const useVehiclesContext = () => {
    const ctx = useContext(VehiclesContext);
    if (!ctx) {
        throw new Error("useVehiclesContext must be used within ");
    } else {
        return ctx;
    }
};

export default VehiclesContext;
